import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Mic, Music, Pencil, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg, mediaUrl } from "@/lib/api";
import { useChannels } from "@/components/ChannelGrid";
import { fieldCls } from "@/components/dashboard/Shared";

const fmtDur = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;
const fmtWhen = (iso) => new Date(iso).toLocaleString("es-AR", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const toLocalInput = (iso) => { const d = new Date(iso); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };

const measure = (src) => new Promise((resolve, reject) => {
  const a = new Audio();
  a.preload = "metadata";
  a.onloadedmetadata = () => (isFinite(a.duration) && a.duration > 0 ? resolve(a.duration) : reject());
  a.onerror = reject;
  a.src = src;
});

const blank = { id: null, title: "", description: "", channel_slug: "", host_id: "", start_at: "", items: [] };

function ItemList({ items, setItems }) {
  const move = (i, j) => { if (j < 0 || j >= items.length) return; const n = [...items]; [n[i], n[j]] = [n[j], n[i]]; setItems(n); };
  if (!items.length) return <p className="text-sm text-slate-500">Agrega canciones y mensajes de voz en el orden en que sonarán.</p>;
  return (
    <div data-testid="show-items" className="space-y-2">
      {items.map((it, i) => (
        <div key={`${it.audio_url}-${i}`} className="flex items-center gap-3 rounded-lg bg-[#181824] px-3 py-2 text-sm">
          {it.type === "track" ? <Music className="h-4 w-4 text-[#FF7A45]" /> : <Mic className="h-4 w-4 text-sky-300" />}
          <span className="min-w-0 flex-1 truncate text-white">{it.title}{it.artist_name && <span className="text-slate-500"> · {it.artist_name}</span>}</span>
          <span className="font-mono text-xs text-slate-500">{fmtDur(it.duration)}</span>
          <button onClick={() => move(i, i - 1)} className="p-1 text-slate-500 hover:text-white" aria-label="Subir"><ArrowUp className="h-3.5 w-3.5" /></button>
          <button onClick={() => move(i, i + 1)} className="p-1 text-slate-500 hover:text-white" aria-label="Bajar"><ArrowDown className="h-3.5 w-3.5" /></button>
          <button data-testid={`show-item-remove-${i}`} onClick={() => setItems(items.filter((_, k) => k !== i))} className="p-1 text-slate-500 hover:text-red-400" aria-label="Quitar"><X className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <p className="font-mono text-xs text-slate-400">Duración total: {fmtDur(items.reduce((a, it) => a + it.duration, 0))}</p>
    </div>
  );
}

function ShowForm({ initial, onSaved, onCancel }) {
  const api = useApi();
  const channels = useChannels();
  const [v, setV] = useState(initial);
  const [tracks, setTracks] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [pick, setPick] = useState("");
  const [msgTitle, setMsgTitle] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api("get", "/admin/tracks").then((d) => setTracks(d.filter((t) => t.radio_status === "approved"))).catch(() => {});
    api("get", "/users").then((d) => setHosts(d.users.filter((u) => ["curator", "admin"].includes(u.role)))).catch(() => {});
  }, [api]);
  const setItems = (items) => setV((p) => ({ ...p, items }));

  const addTrack = async () => {
    const t = tracks.find((x) => x.id === pick);
    if (!t) return;
    setBusy("track");
    try {
      const duration = await measure(mediaUrl(t.audio_url));
      setItems([...v.items, { type: "track", track_id: t.id, title: t.title, artist_name: t.artist_name, audio_url: t.audio_url, duration }]);
      setPick("");
    } catch { toast.error("No pudimos leer la duración de esa canción"); } finally { setBusy(""); }
  };
  const addMessage = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setBusy("msg");
    try {
      const duration = await measure(URL.createObjectURL(file));
      const fd = new FormData(); fd.append("file", file);
      const { audio_url } = await api("post", "/admin/shows/message", fd);
      setItems([...v.items, { type: "message", title: msgTitle || "Mensaje del curador", audio_url, duration }]);
      setMsgTitle("");
    } catch (err) { toast.error(err?.response ? errMsg(err) : "No pudimos leer ese audio"); } finally { setBusy(""); }
  };
  const save = async () => {
    if (!v.title || !v.channel_slug || !v.start_at || !v.items.length) { toast.error("Completa título, canal, horario y al menos un elemento"); return; }
    setBusy("save");
    const body = { title: v.title, description: v.description, channel_slug: v.channel_slug, host_id: v.host_id || null,
      start_at: new Date(v.start_at).toISOString(), items: v.items.map(({ type, track_id, audio_url, title, duration }) => ({ type, track_id, audio_url, title, duration })) };
    try {
      await api(v.id ? "put" : "post", v.id ? `/admin/shows/${v.id}` : "/admin/shows", body);
      toast.success("Programa guardado");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="show-form" className="space-y-4 rounded-xl border border-white/10 bg-[#12121A] p-6">
      <p className="font-heading text-lg font-bold text-white">{v.id ? "Editar programa" : "Nuevo programa"}</p>
      <div className="grid gap-4 md:grid-cols-2">
        <Input data-testid="show-title" placeholder="Nombre del programa" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} className={fieldCls} />
        <Input data-testid="show-start" type="datetime-local" value={v.start_at} onChange={(e) => setV({ ...v, start_at: e.target.value })} className={fieldCls} />
        <Select value={v.channel_slug} onValueChange={(s) => setV({ ...v, channel_slug: s })}>
          <SelectTrigger data-testid="show-channel" className={fieldCls}><SelectValue placeholder="Canal" /></SelectTrigger>
          <SelectContent className="border-white/10 bg-[#181824] text-slate-200">{channels?.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={v.host_id || "none"} onValueChange={(s) => setV({ ...v, host_id: s === "none" ? "" : s })}>
          <SelectTrigger data-testid="show-host" className={fieldCls}><SelectValue placeholder="Curador que lo presenta" /></SelectTrigger>
          <SelectContent className="border-white/10 bg-[#181824] text-slate-200">
            <SelectItem value="none">Sin presentador</SelectItem>
            {hosts.map((h) => <SelectItem key={h.user_id} value={h.user_id}>{h.display_name || h.email}</SelectItem>)}
          </SelectContent>
        </Select>
        <Textarea data-testid="show-description" placeholder="Descripción (opcional)" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} className={`${fieldCls} md:col-span-2`} />
      </div>
      <div className="grid gap-3 rounded-lg border border-white/5 p-4 md:grid-cols-2">
        <div className="flex gap-2">
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger data-testid="show-track-pick" className={fieldCls}><SelectValue placeholder="Elegir canción aprobada" /></SelectTrigger>
            <SelectContent className="max-h-72 border-white/10 bg-[#181824] text-slate-200">{tracks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title} · {t.artist_name}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="show-add-track" disabled={!pick || !!busy} onClick={addTrack} className="rounded-full bg-white text-black hover:bg-slate-200">{busy === "track" ? "..." : "Agregar"}</Button>
        </div>
        <div className="flex items-center gap-2">
          <Input data-testid="show-message-title" placeholder="Título del mensaje de voz" value={msgTitle} onChange={(e) => setMsgTitle(e.target.value)} className={fieldCls} />
          <label className="shrink-0 cursor-pointer rounded-full bg-sky-500/20 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/30">
            {busy === "msg" ? "Subiendo..." : "Subir voz"}
            <input data-testid="show-message-input" type="file" accept="audio/mpeg,audio/wav,audio/x-wav,.mp3,.wav" className="hidden" disabled={!!busy} onChange={addMessage} />
          </label>
        </div>
      </div>
      <ItemList items={v.items} setItems={setItems} />
      <div className="flex gap-2">
        <Button data-testid="show-save" disabled={!!busy} onClick={save} className="rounded-full bg-[#FF4500] text-white hover:bg-[#ff5a1f]">{busy === "save" ? "Guardando..." : "Guardar programa"}</Button>
        {onCancel && <Button variant="outline" onClick={onCancel} className="rounded-full border-white/20 bg-transparent text-slate-200">Cancelar</Button>}
      </div>
    </div>
  );
}

export function ShowsAdmin() {
  const api = useApi();
  const [shows, setShows] = useState([]);
  const [editing, setEditing] = useState(blank);
  const [formKey, setFormKey] = useState(0);
  const reload = () => api("get", "/admin/shows").then(setShows).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); const t = setInterval(reload, 30000); return () => clearInterval(t); }, []);
  const reset = () => { setEditing({ ...blank }); setFormKey((k) => k + 1); };
  const del = async (s) => {
    if (!window.confirm(`¿Eliminar "${s.title}"?`)) return;
    try { await api("delete", `/admin/shows/${s.id}`); toast.success("Programa eliminado"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const edit = (s) => setEditing({ ...s, host_id: s.host_id || "", start_at: toLocalInput(s.start_at) });
  const now = new Date().toISOString();
  return (
    <div className="space-y-8">
      <ShowForm key={`${editing.id || "new"}-${formKey}`} initial={editing} onSaved={() => { reset(); reload(); }} onCancel={editing.id ? reset : null} />
      <div data-testid="shows-admin-list" className="rounded-xl border border-white/10 bg-[#12121A] p-2">
        {shows.map((s) => (
          <div key={s.id} data-testid={`show-row-${s.id}`} className="flex flex-wrap items-center gap-4 border-b border-white/5 p-4 last:border-0">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-white">{s.title} {s.start_at <= now && s.end_at > now && <span className="ml-1 rounded-full bg-[#FF4500] px-2 py-0.5 font-mono text-[10px] text-white">EN VIVO</span>}{s.end_at <= now && <span className="ml-1 font-mono text-[10px] text-slate-500">FINALIZADO</span>}</p>
              <p className="text-xs text-slate-500">{fmtWhen(s.start_at)} → {new Date(s.end_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })} · {s.channel_name} · {s.host_name || "sin presentador"} · {s.items.length} elementos</p>
            </div>
            <button data-testid={`show-edit-${s.id}`} onClick={() => edit(s)} className="p-2 text-slate-400 hover:text-white" aria-label="Editar"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`show-delete-${s.id}`} onClick={() => del(s)} className="p-2 text-slate-500 hover:text-red-400" aria-label="Eliminar"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!shows.length && <p className="p-6 text-center text-sm text-slate-500">Todavía no hay programas.</p>}
      </div>
    </div>
  );
}
